import * as React from "react"

import { cn } from "@/lib/utils"
import { Icon } from "@/components/icon"
import type { MaterialSymbolName } from "@/components/icon/glyphs"

/**
 * SRAM select (CRCB2B-633). Stays a native `<select>`: Figma composes the field
 * from a `Dropdown` panel plus an `Icon Button` chevron, and a native select can
 * express neither — the option list is OS-drawn and the element cannot contain
 * an interactive child. The field box is matched; the panel is out of scope.
 *
 * The chevron is the Material Symbols `arrow_drop_down` glyph (verified on
 * Figma `5878:41518`), rendered as a decorative overlay — `pointer-events-none`
 * and `aria-hidden`, visually equivalent to Figma's icon button but structurally
 * not a button. It sits in a 32px box 4px from the right edge, per the design's
 * `pr-[var(--spacing-xs,4px)]`.
 *
 * Sizes match the system scale: `lg` 48px (default, 24px chevron) and `md` 40px
 * (20px chevron). There is no `sm` — the design defines no 32px field.
 *
 * `leadingIcon` is Figma's optional `Decorator` slot (`7031:58898`, hidden by
 * default): a plain 24px glyph — NOT the 32px icon-button box the chevron uses —
 * on --text-color-on-fill-neutral-subtle (--input-foreground), 16px from the
 * left edge with an 8px gap before the value. Unlike Text Input and Text Area,
 * which host their icon slots through `ui/input-group.tsx`, the Select renders
 * it as a second absolutely-positioned overlay: a native `<select>` cannot have
 * DOM siblings inside its own box, which is the same constraint that already
 * forces the chevron overlay (see the note above). The value's inset grows to
 * clear it: 16 + 24 + 8 = 48px at `lg`, 16 + 20 + 8 = 44px at `md`.
 */
function NativeSelect({
  className,
  size = "lg",
  leadingIcon,
  ...props
}: Omit<React.ComponentProps<"select">, "size"> & {
  size?: "lg" | "md"
  leadingIcon?: MaterialSymbolName
}) {
  return (
    <div
      className="group/native-select relative w-fit"
      data-slot="native-select-wrapper"
    >
      <select
        data-slot="native-select"
        data-size={size}
        className={cn(
          "border-input text-input-foreground placeholder:text-placeholder selection:bg-primary selection:text-primary-foreground h-12 w-full min-w-0 appearance-none rounded-ui border bg-muted py-3 pl-4 pr-11 text-base leading-[1.5] tracking-[0.03em] shadow-ui transition-[color,box-shadow] outline-none data-[size=md]:h-10",
          // `md` reaches 40px by reducing the PADDING, never the type — the value text
          // stays 16px/1.5 at both sizes so a `md` and an `lg` field read identically.
          //
          // Without this the arithmetic does not fit and the text clips: 24px of line
          // box plus `py-3`'s 24px of padding needs 48px, which is exactly `lg`. At
          // `h-10` there are only 40px, so the descenders were cut off. 8px top and
          // bottom is what actually fits (24 + 16 = 40).
          "data-[size=md]:py-2",
          // Options are drawn by the OS, but where the browser does honour a colour it
          // inherits the field's `--muted` fill and the list comes out grey. The list
          // is a surface in its own right and belongs on `--background`.
          "[&_option]:bg-background [&_optgroup]:bg-background",
          leadingIcon && (size === "md" ? "pl-11" : "pl-12"),
          "hover:border-border-hover",
          "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          // Border-only invalid treatment, matching Input.
          "aria-invalid:border-border-error",
          // Disabled recolors rather than fades; see input.tsx. There is no readonly
          // treatment here — a native <select> has no readonly attribute.
          // Keeps the resting --muted fill when disabled; see input.tsx.
          "disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-border-disabled disabled:text-disabled-foreground",
          className
        )}
        {...props}
      />
      {leadingIcon && (
        <Icon
          name={leadingIcon}
          size={size === "md" ? 20 : 24}
          className="text-input-foreground pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 select-none"
          data-part="native-select-decorator"
        />
      )}
      {/*
       * The chevron is 24px at BOTH sizes (CRCB2B-633 review), unlike the leading
       * decorator which follows Figma's per-size icon scale. It is an affordance
       * rather than content: it says "this opens a list", and shrinking it with the
       * field made the smaller select look like a different control. Keeping one size
       * also means the 32px box and the `pr-11` inset are identical at both steps.
       *
       * `flex items-center justify-center` centres the glyph in that 32px box. That
       * only works because `display` now lives in `@layer base` in `icons.css` — while
       * it sat in the unlayered rule it beat this utility outright and the glyph sat
       * off-centre. See the note in that file.
       */}
      <Icon
        name="arrow_drop_down"
        size={24}
        className="text-foreground pointer-events-none absolute top-1/2 right-1 flex size-8 -translate-y-1/2 items-center justify-center select-none"
        data-part="native-select-icon"
      />
    </div>
  )
}

function NativeSelectOption({ ...props }: React.ComponentProps<"option">) {
  return <option data-slot="native-select-option" {...props} />
}

function NativeSelectOptGroup({
  className,
  ...props
}: React.ComponentProps<"optgroup">) {
  return (
    <optgroup
      data-slot="native-select-optgroup"
      className={cn(className)}
      {...props}
    />
  )
}

export { NativeSelect, NativeSelectOptGroup, NativeSelectOption }
